<template>
  <v-dialog v-model="showForm" scrollable max-width="780px" @click:outside="onCancelClick">
    <template v-slot:activator="{ on, attrs }">
      <!--addXLButton-->
      <v-btn v-if="buttonType === 'addXLButton'" color="primary" rounded class="elevation-0 monospace-font white--text" v-bind="attrs" v-on="on"><v-icon>add</v-icon>{{ $t("documents.uploadDocument") }}</v-btn>
      <!--editButton-->
      <v-btn v-if="buttonType === 'editButton'" elevation="0" icon x-small class="ms-1" color="grey lighten-1" v-bind="attrs" v-on="on"><v-icon small>edit</v-icon></v-btn>
    </template>
    <v-card>
      <v-card-title class="secondary white--text d-flex align-center justify-space-between">
        <span class="header-font" style="font-size: 22px;">
          <v-icon style="font-size: 18px;" color="white" class="mr-2">add</v-icon>
          {{ this.$t('documents.uploadDocument') }}
        </span>
      </v-card-title>
      <v-card-text>
        <v-row class="py-0 mt-5">
           <!--Type-->
          <v-col cols="12" :md="[2, 8].includes(document.typeId) ? 6 : 12" class="py-0">
            <p class="body-1 font-weight-bold primary--text mb-0">
              {{ $t('general.type') }}
              <v-tooltip top color="lightSecondary lighten-4" max-width="300" v-if="isEditing">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon class="ms-1" x-small color="secondary" v-on="on" v-bind="attrs">info</v-icon>
                </template>
                <span class="black--text body-1">{{ $t('disabledTooltips.notEditable') }}</span>
              </v-tooltip>
            </p>
            <v-autocomplete clearable dense outlined item-value="id" item-text="code" :class="$vuetify.breakpoint.xs ? 'mb-2' : 'me-2'"
              :disabled="isEditing"
              class="slim-input"
              v-model="document.typeId"
              :items="documentTypes"
              :hint="$t('scoutingCalendar.tasks.accommodation.required')" :persistent-hint="!document.typeId"
              @change="handleTypeChange"
            >
              <template v-slot:selection="{item}">
                <v-card-text class="pa-0 primary--text font-weight-medium body-1">
                  <v-icon class="me-2" small>{{ getDocumentIconById(item.id) }}</v-icon>{{ $t(`documents.types.${item.code}`) }}
                </v-card-text>
              </template>
              <template v-slot:item="{item}"><span><v-icon class="me-2" small>{{ getDocumentIconById(item.id) }}</v-icon>{{ $t(`documents.types.${item.code}`) }}</span></template>  
            </v-autocomplete>
          </v-col>
          <!--Game-->
          <v-col cols="12" md="6" class="py-0" v-if="document.typeId === 2">
            <p class="body-1 font-weight-bold primary--text mb-0">
              {{ $t('documents.match') }}
              <v-tooltip top color="lightSecondary lighten-4" max-width="300" v-if="isEditing">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small color="secondary" v-on="on" v-bind="attrs">info</v-icon>
                </template>
                <span class="black--text body-1">{{ $t('disabledTooltips.notEditable') }}</span>
              </v-tooltip>
            </p>
            <v-autocomplete clearable dense outlined item-value="id" item-text="name" :class="$vuetify.breakpoint.xs ? 'mb-2' : ''"
              class="slim-input"
              v-model="document.gameId"
              :items="matchItems"
              :loading="loading"
              :hint="$t('scoutingCalendar.tasks.accommodation.required')" :persistent-hint="!document.gameId"
              :disabled="isEditing"
            >
              <template v-slot:selection="{item}"><v-card-text class="pa-0 primary--text font-weight-medium body-1">{{ item.name }}</v-card-text></template>
              <template v-slot:item="{item}"><span>{{ item.name }}</span></template>  
            </v-autocomplete>
          </v-col>
          <!--Player-->
          <v-col cols="12" md="6" class="py-0" v-if="document.typeId === 8">
            <p class="body-1 font-weight-bold primary--text mb-0">
              {{ $t('general.player') }}
              <v-tooltip top color="lightSecondary lighten-4" max-width="300" v-if="isEditing">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small color="secondary" v-on="on" v-bind="attrs">info</v-icon>
                </template>
                <span class="black--text body-1">{{ $t('disabledTooltips.notEditable') }}</span>
              </v-tooltip>
            </p>
            <v-autocomplete clearable dense outlined item-value="playerId" item-text="playerName" :class="$vuetify.breakpoint.xs ? 'mb-2' : ''"
              class="slim-input"
              v-model="document.playerId"
              :items="startingTeams"
              :loading="loading"
              :hint="$t('scoutingCalendar.tasks.accommodation.required')" :persistent-hint="!document.playerId"
              :disabled="isEditing"
            >
              <template v-slot:selection="{item}"><v-card-text class="pa-0 primary--text font-weight-medium body-1">{{ item.playerName }}</v-card-text></template>
              <template v-slot:item="{item}">
                <v-list-item-title>{{ item.playerName }}</v-list-item-title>
                <v-list-item-subtitle class="d-flex align-center">
                  <img :src="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${item.teamId}.png`" height="15" @error="onImageLoadFailure($event)" />
                  <span class="ms-1">{{ item.teamName }}</span>
                </v-list-item-subtitle>
              </template>  
            </v-autocomplete>
          </v-col>
           <!--File-->
          <v-col cols="12" class="py-0">
            <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('general.file') }}</p>
            <v-file-input dense outlined prepend-icon prepend-inner-icon="$file" show-size truncate-length="40" clearable
              v-model="document.file"
              @change="onFileChange($event)"
              @click:prepend-inner="$refs.mainFileInput.$el.querySelector('input').click()"
              ref="mainFileInput"
              :placeholder="isEditing && document.name ? document.name : $t('general.uploadFile')"
              :hint="$t('scoutingCalendar.tasks.accommodation.required')" :persistent-hint="!isEditing && !document.file"
            ></v-file-input>
          </v-col>
          <!--Notes-->
          <v-col cols="12" class="py-0">
            <p class="body-1 font-weight-bold primary--text mb-0 mt-3">{{ $t('general.notes') }}</p>
            <v-textarea counter outlined :placeholder="$t('documents.notesPlaceholderAlt')" :class="$vuetify.breakpoint.xs ? 'mb-2' : 'flex-grow-1'"
              :rules="rules" 
              v-model="document.notes">
            </v-textarea>
          </v-col>
        </v-row>
      </v-card-text>
      <!--Buttons-->
      <v-card-actions class="d-flex justify-end pa-6">
        <v-btn :color="$vuetify.theme.dark ? 'white' : 'secondary'" rounded outlined class="elevation-0 monospace-font px-4" @click="onCancelClick" :disabled="isSaving">{{$t('general.cancel')}}</v-btn>
        <v-btn rounded class="elevation-0 monospace-font white--text px-4" color="primary" @click="onUploadDocumentClick" :loading="isSaving" 
          :disabled="(!isEditing && !document.file) || !document.typeId || (document.typeId === 2 && !document.gameId)"
        >{{$t('general.save')}}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

import * as _ from 'lodash';
import DocumentService from '../../../services/document.service';
import PlayerService from '../../../services/player/player.service';
import GameService from '../../../services/game/game.service';
import { getDocumentIconById } from '../../../utils/functions';

export default {
  name: 'AddDocument',
  props: {
    buttonType: {
      type: String,
      default: 'addXLButton' //Types: 'addXLButton', 'addXSButton', editButton
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    documentToUpdate: {
      type: Object,
      default: () => {
        return {};
      }
    },
    documentTypes: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: 'player'
    }
  },
  data() {
    return {
      showForm: false,
      isSaving: false,
      document: { typeId: null, gameId: null, playerId: null, file: null, notes: '' },
      rules: [(v) => v.length <= 1000 || this.$t('scoutingAnalysis.charactersMax')],
      matchItems: [],
      startingTeams: [],
      loading: false
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    darkMode(){
      return this.currentUser.darkMode || this.$vuetify.theme.dark;
    },
  },
  watch: {
    'document.typeId'(newValue) {
      if (newValue !== 2) {
        this.document.gameId = null; 
      } else if (newValue !== 8) {
        this.document.playerId = null; 
      };
    },
  },
  created() {
    if (this.isEditing) {  //editing
      this.document = _.cloneDeep(this.documentToUpdate); //Keep original content in a different object
      this.document.file = this.documentToUpdate.file;
      this.document.gameId = this.documentToUpdate.gameId;
      this.handleTypeChange();
      if (!this.documentToUpdate.notes) this.document.notes = '';
      if (this.type === 'game' && this.documentToUpdate.typeId === 2) {
        this.document.playerId = this.documentToUpdate.playerId;
        this.document.typeId = 8;
      };
    } else { //new document
      if (this.type === 'player' && this.$route.params.id) this.document.playerId = +this.$route.params.id;
      else if (this.type === 'team' && this.$route.params.id) this.document.teamId = +this.$route.params.id;
      else if (this.type === 'game' && this.$route.params.id) this.document.gameId = +this.$route.params.id;
    };
  },
  methods: {
    onCancelClick() {
      //Reset document values
      this.document = { notes: '' };
      this.matchItems = [];
      this.$emit("dialogClosed");
      this.isSaving = false;
      this.showForm = !this.showForm;
    },
    async onUploadDocumentClick() {
      this.isSaving = true;
      const postSearchEvent = { operation: "create", filename: this.document.name, documentType: this.documentTypes.filter(d => d.id === this.document.typeId)[0] };
      postSearchEvent.operation = this.isEditing ? "update" : "create";

      if (this.isEditing) await this.editDocument();
      else await this.saveDocument();
      
      this.$emit("postSearch", postSearchEvent);
      this.onCancelClick();
    },
    async saveDocument() {
      let documentResponse;
      let urlResponse;
      if (this.type === 'player') { //save player document
        documentResponse = await DocumentService.postDocument(this.document);
        if (documentResponse.data) { 
          urlResponse = await DocumentService.getUploadURL(documentResponse.data, this.document.name);
          await DocumentService.upload(urlResponse.data, this.document.file);
        };
      } else if (this.type === 'team') { //save team document
        documentResponse = await DocumentService.postTeamDocument(this.document);
        if (documentResponse.data) {
          urlResponse = await DocumentService.getTeamUploadURL(documentResponse.data, this.document.name);
          await DocumentService.upload(urlResponse.data, this.document.file);
        };
      } else if (this.type === 'game') { //save game document
        if (this.document.typeId === 8) {
          this.document.typeId = 2; 
          documentResponse = await DocumentService.postDocument(this.document);
        } else documentResponse = await DocumentService.postGameDocument(this.document);

        if (documentResponse.data) {
          if (this.document.typeId === 2) urlResponse = await DocumentService.getUploadURL(documentResponse.data, this.document.name); //player_report
          else urlResponse = await DocumentService.getGameUploadURL(documentResponse.data, this.document.name);
          await DocumentService.upload(urlResponse.data, this.document.file);
        };
      };
    },
    async editDocument() {
      let documentResponse;
      let urlResponse;
      if (this.type === 'player') { //edit player document
        documentResponse = await DocumentService.updateDocument(this.document);
        if (documentResponse.data && this.document.file) {
          urlResponse = await DocumentService.getUploadURL(documentResponse.data, this.document.name);
          await DocumentService.upload(urlResponse.data, this.document.file);
        };
      } else if (this.type === 'team') { //edit team document
        documentResponse = await DocumentService.updateTeamDocument(this.document);
        if (documentResponse.data && this.document.file) {
          urlResponse.data = await DocumentService.getTeamUploadURL(documentResponse.data, this.document.name);
          await DocumentService.upload(documentResponse.data, this.document.file);
        }; 
      } else if (this.type === 'game') {  //edit game document
        if ([2, 8].includes(this.document.typeId)) { //player_report
          documentResponse = await DocumentService.updateDocument(this.document); 
        } else documentResponse = await DocumentService.updateGameDocument(this.document);
        
        if (documentResponse.data && this.document.file) {
          if (this.document.typeId === 2) urlResponse = await DocumentService.getUploadURL(documentResponse.data, this.document.name); //player_report
          else urlResponse = await DocumentService.getGameUploadURL(documentResponse.data, this.document.name);
          await DocumentService.upload(urlResponse.data, this.document.file);
        };
      };
    },
    getDocumentIconById(id) {
      return getDocumentIconById(id);
    },
    async getMatchItems() {
      const gamesData = await PlayerService.getGamesPlayedOfPlayer(this.document.playerId, 20);
      this.matchItems = gamesData.data.map(match => {
        return { id: match.gameId, name: `${match.homeTeamName} ${match.homeTeamScore} : ${match.awayTeamName} ${match.awayTeamScore}` };
      });
    },
    async handleTypeChange() {
      this.loading = true;
      if (this.type === 'player') {
        this.document.gameId = null;
        if (this.document.typeId === 2 && !this.matchItems.length) await this.getMatchItems();
      } else if (this.type === 'team') {
        this.document.gameId = null;
        if (this.document.typeId === 2 && !this.matchItems.length) await this.loadLatestGamesPlayed();
      } else if (this.type === 'game') {
        this.document.playerId = null;
        if ([2, 8].includes(this.document.typeId) && !this.startingTeams.length) await this.loadStartingTeams();
      };
      this.loading = false;
    },
    onFileChange(e) {
      const file = e;
      this.document.name = file ? file.name : null;
      this.document.file = file;
    },
    async loadLatestGamesPlayed() {
      const gamesData = await GameService.getLatestGamesPlayed(this.document.teamId, 10);
      this.matchItems = gamesData.data.map(match => {
        return { id: match.gameId, name: `${match.homeTeamName} ${match.homeTeamScore} : ${match.awayTeamName} ${match.awayTeamScore}` };
      });
    },
    async loadStartingTeams() {
      const gameResponse = await GameService.getStartingTeamsOfGame(this.document.gameId || +this.$route.params.id);
      this.startingTeams = gameResponse.data;
    },
    onImageLoadFailure(event){
      event.target.src = "https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png";
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/views/scouting-analysis/documents/AddDocument.vue